import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Printer, 
  Package, 
  Search, 
  Globe, 
  Palette, 
  ShoppingBag,
  Check
} from 'lucide-react';

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          const yOffset = -120;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      id: 'print-on-demand',
      title: 'Print on Demand',
      icon: Printer,
      description: 'Custom printing solutions with no minimum orders. We handle printing, quality control, and shipping while you focus on growing your brand.',
      features: ['No minimum order quantities', 'Wide range of printable products', 'High-quality printing technology', 'Fast turnaround times', 'Automated order processing', 'Custom packaging options'],
      image: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=600'
    },
    {
      id: 'dropshipping',
      title: 'Dropshipping Fulfillment',
      icon: Package,
      description: 'Seamless order fulfillment from warehouse to customer doorstep. We pick, pack, and ship your orders with care and precision.',
      features: ['Same-day order processing', 'Real-time inventory sync', 'Multiple shipping options', 'Branded packing slips', 'Return management', 'Order tracking integration'],
      image: 'https://images.pexels.com/photos/4246120/pexels-photo-4246120.jpeg?auto=compress&cs=tinysrgb&w=600'
    },
    {
      id: 'product-sourcing',
      title: 'Product Sourcing',
      icon: Search,
      description: 'Find reliable suppliers and quality products at competitive prices. We handle negotiations, inspections, and import logistics.',
      features: ['Vetted supplier network', 'Quality inspection services', 'Competitive price negotiations', 'Sample procurement', 'Import/export assistance', 'Product customization'],
      image: 'https://images.pexels.com/photos/5632398/pexels-photo-5632398.jpeg?auto=compress&cs=tinysrgb&w=600'
    },
    {
      id: 'global-warehousing',
      title: 'Global Warehousing',
      icon: Globe,
      description: 'Strategic warehouse locations worldwide. Reduce shipping times and costs while improving customer satisfaction.',
      features: ['Multiple warehouse locations', 'Climate-controlled storage', 'Real-time inventory tracking', 'Automated reorder alerts', 'Kitting and bundling', 'Cross-docking capabilities'],
      image: 'https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=600'
    },
    {
      id: 'custom-branding',
      title: 'Custom Branding',
      icon: Palette,
      description: 'Create a memorable unboxing experience. From custom packaging to branded inserts, elevate your brand presence.',
      features: ['Custom packaging design', 'Branded shipping materials', 'Thank you cards & inserts', 'Custom labels and stickers', 'Gift wrapping options', 'Sustainable packaging'],
      image: 'https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=600'
    },
    {
      id: 'tiktok-shop',
      title: 'TikTok Shop Solutions',
      icon: ShoppingBag,
      description: 'Specialized fulfillment for social commerce. Fast, reliable fulfillment to keep up with viral trends.',
      features: ['Rapid order processing', 'TikTok Shop integration', 'Trending product fulfillment', 'Live stream order handling', 'Influencer collaboration', 'Flash sale capabilities'],
      image: 'https://images.pexels.com/photos/5632388/pexels-photo-5632388.jpeg?auto=compress&cs=tinysrgb&w=600'
    }
  ];

  return (
    <div data-testid="services-page" className="bg-[#fffdf8]">
      {/* Hero */}
      <section className="pt-32 pb-20 warm-gradient">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
              Our Services
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl lg:text-7xl font-semibold text-[#78350f] mb-6" data-testid="services-hero-title">
              Comprehensive
              <br />
              <span className="text-[#ea580c]">Solutions</span>
            </h1>
            <p className="text-[#78350f]/80 text-lg">
              From Print on Demand to Global Warehousing, we provide everything 
              you need to scale your ecommerce business naturally.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services - Zigzag Layout */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          data-testid={`service-section-${service.id}`}
          className={`py-24 ${index % 2 === 0 ? 'bg-[#fffdf8]' : 'bg-[#fef3c7]/30'}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? 'lg:col-start-2' : ''}
              >
                <div className="w-14 h-14 bg-[#ea580c] rounded-2xl flex items-center justify-center mb-6">
                  <service.icon size={28} className="text-white" />
                </div>
                <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-[#78350f] mb-4">
                  {service.title}
                </h2>
                <p className="text-[#78350f]/80 text-lg mb-8">
                  {service.description}
                </p>

                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-[#fef3c7] rounded-full flex items-center justify-center flex-shrink-0">
                        <Check size={12} className="text-[#ea580c]" />
                      </div>
                      <span className="text-[#78350f]/80 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  data-testid={`cta-${service.id}`}
                  className="inline-flex items-center gap-2 bg-[#ea580c] text-white px-6 py-3 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg group organic-shape-3"
                >
                  Get Started
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}
              >
                <div className={`${index % 2 === 0 ? 'organic-shape-1' : 'organic-shape-2'} overflow-hidden warm-shadow-lg`}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-24 bg-[#78350f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-white mb-6">
              Need a Custom{' '}
              <span className="text-[#fef3c7]">Solution?</span>
            </h2>
            <p className="text-[#fcd9b8] text-lg mb-10">
              Our team can create a customized fulfillment solution 
              tailored to your specific business requirements.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-contact"
              className="inline-flex items-center gap-2 bg-[#ea580c] text-white px-8 py-4 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl group organic-shape-3"
            >
              Contact Our Team
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
